import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Mail, Check, Loader2 } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/**
 * /account/email-preferences?token=...  (linked from the family weekly
 * recap footer). One-click pause — token is opaque per user. No login
 * required; failures fall back to the regular Account preferences page.
 */
export default function EmailPreferences() {
    const [params] = useSearchParams();
    const token = params.get('token');
    const [state, setState] = useState('idle'); // idle | working | paused | resubscribed | error
    const [err, setErr] = useState(null);

    useSEO({ title: 'Email preferences | Linguistic Quest', path: '/account/email-preferences', noindex: true });

    const pause = async () => {
        if (!token) { setErr('No token in URL. Please use the link from your email.'); return; }
        setState('working');
        try {
            await api.post('/email-preferences/weekly-recap', { token, weekly_recap_consent: false });
            setState('paused');
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not update preferences');
            setState('error');
        }
    };
    const resubscribe = async () => {
        if (!token) { setErr('No token in URL.'); return; }
        setState('working');
        try {
            await api.post('/email-preferences/weekly-recap', { token, weekly_recap_consent: true });
            setState('resubscribed');
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not update preferences');
            setState('error');
        }
    };

    // Auto-pause on first load if token present (one-click pause).
    useEffect(() => {
        if (token && state === 'idle') pause();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <main className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="page-email-preferences">
            <div className="inline-flex h-14 w-14 rounded-full bg-lq-purple-100 items-center justify-center text-lq-purple-700">
                <Mail size={28}/>
            </div>
            <h1 className="mt-4 font-display text-3xl font-semibold text-lq-ink-black">Email preferences</h1>

            {state === 'working' && (
                <p className="mt-4 text-lq-ink-500 inline-flex items-center gap-2 justify-center"><Loader2 className="animate-spin" size={16}/> Updating…</p>
            )}

            {state === 'paused' && (
                <div className="mt-6 bg-white border border-lq-success/30 rounded-2xl p-6">
                    <div className="inline-flex h-10 w-10 rounded-full bg-lq-success/15 items-center justify-center text-lq-success mb-2">
                        <Check size={20}/>
                    </div>
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900">Sunday family recaps paused</h2>
                    <p className="mt-2 text-lq-ink-700 text-sm">
                        We won't send you any more weekly digests. Transactional emails (receipts, password resets) still arrive as needed.
                    </p>
                    <p className="mt-4 text-lq-ink-500 text-sm italic">Pace, not pressure — your family decides when to play.</p>
                    <button onClick={resubscribe} className="mt-5 text-sm text-lq-purple-700 hover:underline" data-testid="resubscribe-link">
                        Changed your mind? Turn weekly recaps back on
                    </button>
                </div>
            )}

            {state === 'resubscribed' && (
                <div className="mt-6 bg-white border border-lq-purple-200 rounded-2xl p-6">
                    <div className="inline-flex h-10 w-10 rounded-full bg-lq-purple-100 items-center justify-center text-lq-purple-700 mb-2">
                        <Check size={20}/>
                    </div>
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900">Welcome back to Sunday recaps</h2>
                    <p className="mt-2 text-lq-ink-700 text-sm">You'll receive your next family digest this coming Sunday.</p>
                </div>
            )}

            {state === 'error' && (
                <div className="mt-6 bg-white border border-lq-error/30 rounded-2xl p-6">
                    <h2 className="font-display text-xl font-semibold text-lq-error">Hmm, something's off</h2>
                    <p className="mt-2 text-lq-ink-700 text-sm">{err}</p>
                    <Link to="/account" className="mt-4 inline-block text-sm text-lq-purple-700 hover:underline">Manage all email preferences from /account</Link>
                </div>
            )}

            <p className="mt-8 text-xs text-lq-ink-500">
                <Link to="/" className="hover:underline">Back to Linguistic Quest</Link>
            </p>
        </main>
    );
}
